import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  RefreshCw, CheckCircle, Users, Briefcase, X, DollarSign, 
  Trash2, Archive, FileText, MessageSquare, CheckSquare, Square, Banknote, Mail,
  Tag, ScrollText, Star, Sparkles, Receipt, Heart, RotateCcw, Truck, CreditCard,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { archiveAPI } from '../../services/api';

// Archive categories for organized display
const ARCHIVE_CATEGORIES = {
  customers: {
    label: 'Customers',
    icon: Users,
    tabs: ['completed', 'cancelled', 'conversations', 'reviews']
  },
  workers: {
    label: 'Workers', 
    icon: Briefcase,
    tabs: ['workers', 'applications', 'jobs', 'paystubs', 'idcards']
  },
  finance: {
    label: 'Finance',
    icon: DollarSign,
    tabs: ['transactions', 'receipts', 'tips']
  },
  services: {
    label: 'Services & Products',
    icon: Tag,
    tabs: ['services', 'coupons', 'courier']
  },
  communications: {
    label: 'Communications',
    icon: Mail,
    tabs: ['emails']
  },
  documents: {
    label: 'Documents',
    icon: FileText,
    tabs: ['policies']
  }
};

const ArchiveTab = ({
  archiveSummary,
  archiveTab,
  setArchiveTab,
  archiveLoading,
  fetchArchiveData,
  fetchAllData,
  completedJobs,
  deletedWorkers,
  deletedJobs,
  cancelledBookings,
  deletedTransactions,
  deletedApplications,
  archivedConversations = [],
  archivedPaystubs = [],
  archivedEmails = [],
  deletedServices = [],
  deletedPolicies = [],
  deletedCoupons = [],
  deletedReviews = [],
  archivedReceipts = [],
  archivedTips = [],
  archivedCourierJobs = [],
  archivedIDCards = [],
  onRestoreReceipt,
  onRestoreTip,
  onRestoreCourierJob,
  onRestoreIDCard,
  onPermanentDeleteIDCard
}) => {
  // Multi-select state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [viewMode, setViewMode] = useState('categories'); // 'categories' or 'all'

  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Get count for a specific archive tab
  const getTabCount = (tab) => {
    const counts = {
      completed: archiveSummary.completed_jobs || 0,
      paystubs: (archiveSummary.archived_paystubs || 0) + (archiveSummary.paid_paystubs || 0),
      workers: archiveSummary.deleted_workers || 0,
      jobs: archiveSummary.deleted_jobs || 0,
      cancelled: archiveSummary.cancelled_bookings || 0,
      transactions: archiveSummary.deleted_transactions || 0,
      applications: archiveSummary.deleted_applications || 0,
      conversations: archiveSummary.archived_conversations || 0,
      emails: archiveSummary.archived_emails || 0,
      idcards: archiveSummary.archived_idcards || 0,
      services: archiveSummary.deleted_services || 0,
      policies: archiveSummary.deleted_policies || 0,
      coupons: archiveSummary.deleted_coupons || 0,
      reviews: archiveSummary.deleted_reviews || 0,
      receipts: archiveSummary.archived_receipts || 0,
      tips: archiveSummary.archived_tips || 0,
      courier: archiveSummary.archived_courier_jobs || 0,
    };
    return counts[tab] || 0;
  };

  // Get category total count
  const getCategoryCount = (category) => {
    return ARCHIVE_CATEGORIES[category].tabs.reduce((sum, tab) => sum + getTabCount(tab), 0);
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = (items) => {
    setSelectedItems(items.map(i => i.id));
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectMode(false);
  };

  // Bulk delete handlers
  const handleBulkPermanentDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`Permanently delete ${selectedItems.length} items? This cannot be undone.`)) return;
    
    try {
      let deleteFunc;
      switch(archiveTab) {
        case 'workers':
          deleteFunc = archiveAPI.permanentDeleteWorker;
          break;
        case 'jobs':
          deleteFunc = archiveAPI.permanentDeleteJob;
          break;
        case 'transactions':
          deleteFunc = archiveAPI.permanentDeleteTransaction;
          break;
        case 'applications':
          deleteFunc = archiveAPI.permanentDeleteApplication;
          break;
        case 'conversations':
          deleteFunc = archiveAPI.permanentDeleteConversation;
          break;
        case 'emails':
          // Use bulk API for emails
          await archiveAPI.bulkPermanentDeleteEmails(selectedItems);
          toast.success(`${selectedItems.length} emails permanently deleted`);
          clearSelection();
          fetchArchiveData();
          return;
        default:
          return;
      }
      
      await Promise.all(selectedItems.map(id => deleteFunc(id)));
      toast.success(`${selectedItems.length} items permanently deleted`);
      clearSelection();
      fetchArchiveData();
    } catch (e) {
      toast.error('Failed to delete some items');
    }
  };

  const handleBulkRestore = async () => {
    if (selectedItems.length === 0) return;
    
    try {
      let restoreFunc;
      switch(archiveTab) {
        case 'workers':
          restoreFunc = (id) => archiveAPI.restoreWorker(id);
          break;
        case 'jobs':
          restoreFunc = (id) => archiveAPI.restoreJob(id);
          break;
        case 'transactions':
          restoreFunc = (id) => archiveAPI.restoreTransaction(id);
          break;
        case 'applications':
          restoreFunc = (id) => archiveAPI.restoreApplication(id, 'Bulk restored');
          break;
        case 'emails':
          // Use bulk API for emails
          await archiveAPI.bulkRestoreEmails(selectedItems);
          toast.success(`${selectedItems.length} emails restored`);
          clearSelection();
          fetchArchiveData();
          fetchAllData();
          return;
        default:
          return;
      }
      
      await Promise.all(selectedItems.map(id => restoreFunc(id)));
      toast.success(`${selectedItems.length} items restored`);
      clearSelection();
      fetchArchiveData();
      fetchAllData();
    } catch (e) {
      toast.error('Failed to restore some items');
    }
  };

  const handleRestoreWorker = async (worker) => {
    try {
      await archiveAPI.restoreWorker(worker.id);
      toast.success('Worker restored');
      fetchArchiveData();
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePermanentDeleteWorker = async (worker) => {
    if (!window.confirm('Permanently delete? This cannot be undone.')) return;
    try {
      await archiveAPI.permanentDeleteWorker(worker.id);
      toast.success('Permanently deleted');
      fetchArchiveData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRestoreJob = async (job) => {
    try {
      await archiveAPI.restoreJob(job.id);
      toast.success('Job restored');
      fetchArchiveData();
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePermanentDeleteJob = async (job) => {
    if (!window.confirm('Permanently delete? This cannot be undone.')) return;
    try {
      await archiveAPI.permanentDeleteJob(job.id);
      toast.success('Permanently deleted');
      fetchArchiveData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleArchiveCancelledBooking = async (booking) => {
    if (!window.confirm('Move to deleted archive?')) return;
    try {
      await archiveAPI.archiveCancelledBooking(booking.id);
      toast.success('Moved to archive');
      fetchArchiveData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
    }
  };

  const handleRestoreTransaction = async (txn) => {
    try {
      await archiveAPI.restoreTransaction(txn.id);
      toast.success('Transaction restored');
      fetchArchiveData();
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePermanentDeleteTransaction = async (txn) => {
    if (!window.confirm('Permanently delete? This cannot be undone.')) return;
    try {
      await archiveAPI.permanentDeleteTransaction(txn.id);
      toast.success('Permanently deleted');
      fetchArchiveData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRestoreApplication = async (app) => {
    try {
      await archiveAPI.restoreApplication(app.id, 'Restored by owner');
      toast.success('Application restored');
      fetchArchiveData();
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePermanentDeleteApplication = async (app) => {
    if (!window.confirm('Permanently delete this application? This cannot be undone.')) return;
    try {
      await archiveAPI.permanentDeleteApplication(app.id);
      toast.success('Permanently deleted');
      fetchArchiveData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handlePermanentDeleteConversation = async (conv) => {
    if (!window.confirm('Permanently delete this conversation? This cannot be undone.')) return;
    try {
      await archiveAPI.permanentDeleteConversation(conv.id);
      toast.success('Permanently deleted');
      fetchArchiveData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRestoreEmail = async (email) => {
    try {
      await archiveAPI.bulkRestoreEmails([email.id]);
      toast.success('Email restored');
      fetchArchiveData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePermanentDeleteEmail = async (email) => {
    if (!window.confirm('Permanently delete this email log? This cannot be undone.')) return;
    try {
      await archiveAPI.bulkPermanentDeleteEmails([email.id]);
      toast.success('Permanently deleted');
      fetchArchiveData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // Get current items for selection
  const getCurrentItems = () => {
    switch(archiveTab) {
      case 'workers': return deletedWorkers;
      case 'jobs': return deletedJobs;
      case 'transactions': return deletedTransactions;
      case 'applications': return deletedApplications;
      case 'conversations': return archivedConversations;
      case 'paystubs': return archivedPaystubs;
      case 'emails': return archivedEmails;
      case 'services': return deletedServices;
      case 'policies': return deletedPolicies;
      case 'coupons': return deletedCoupons;
      case 'reviews': return deletedReviews;
      default: return [];
    }
  };

  // Check if current tab supports restore
  const supportsRestore = ['workers', 'jobs', 'transactions', 'applications', 'emails'].includes(archiveTab);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Archive</h1>
        <div className="flex gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-[var(--background-subtle)] rounded-lg p-1">
            <button
              onClick={() => setViewMode('categories')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                viewMode === 'categories' ? 'bg-[var(--background-paper)] shadow text-[var(--primary)]' : 'text-[var(--text-secondary)]'
              }`}
            >
              By Category
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                viewMode === 'all' ? 'bg-[var(--background-paper)] shadow text-[var(--primary)]' : 'text-[var(--text-secondary)]'
              }`}
            >
              All Items
            </button>
          </div>
          
          {selectMode && selectedItems.length > 0 && (
            <>
              {supportsRestore && (
                <Button onClick={handleBulkRestore} variant="outline" size="sm" className="text-[var(--success)] border-green-300">
                  Restore ({selectedItems.length})
                </Button>
              )}
              <Button onClick={handleBulkPermanentDelete} variant="destructive" size="sm">
                <Trash2 className="w-4 h-4 mr-1" />
                Delete ({selectedItems.length})
              </Button>
              <Button onClick={clearSelection} variant="ghost" size="sm">
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
          {!selectMode && archiveTab !== 'completed' && archiveTab !== 'cancelled' && (
            <Button 
              onClick={() => setSelectMode(true)} 
              variant="outline" 
              size="sm"
            >
              <CheckSquare className="w-4 h-4 mr-2" />
              Select Multiple
            </Button>
          )}
          <Button onClick={fetchArchiveData} variant="outline" size="sm" disabled={archiveLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${archiveLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Category View */}
      {viewMode === 'categories' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {Object.entries(ARCHIVE_CATEGORIES).map(([key, category]) => {
            const isCollapsed = collapsedCategories[key];
            const count = getCategoryCount(key);
            const Icon = category.icon;
            
            return (
              <div key={key} className="card overflow-hidden">
                <button
                  onClick={() => toggleCategory(key)}
                  className="w-full flex items-center justify-between p-4 bg-[var(--background-subtle)] hover:bg-[var(--background-subtle)] transition-colors"
                >
                  <span className="flex items-center gap-2 font-semibold">
                    <Icon className="w-5 h-5 text-[var(--primary)]" />
                    {category.label}
                    {count > 0 && (
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-[var(--primary)] text-white">
                        {count}
                      </span>
                    )}
                  </span>
                  {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {!isCollapsed && (
                  <div className="p-3 space-y-2">
                    {category.tabs.map(tab => {
                      const tabCount = getTabCount(tab);
                      return (
                        <button
                          key={tab}
                          onClick={() => { setArchiveTab(tab); clearSelection(); setViewMode('all'); }}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
                            archiveTab === tab 
                              ? 'bg-[var(--primary)] text-white' 
                              : 'hover:bg-[var(--background-subtle)] text-[var(--text-primary)]'
                          }`}
                        >
                          <span className="capitalize">{tab.replace('_', ' ')}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            archiveTab === tab ? 'bg-[var(--background-paper)] text-[var(--primary)]' : 'bg-[var(--background-subtle)]'
                          }`}>
                            {tabCount}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* All Items View - Summary Cards */}
      {viewMode === 'all' && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'completed' ? 'ring-2 ring-green-400' : ''}`}
          onClick={() => { setArchiveTab('completed'); clearSelection(); }}
        >
          <CheckCircle className="w-5 h-5 mx-auto mb-1 text-[var(--success)]" />
          <p className="text-xl font-bold text-[var(--success)]">{archiveSummary.completed_jobs || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Completed Jobs</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'paystubs' ? 'ring-2 ring-emerald-400' : ''}`}
          onClick={() => { setArchiveTab('paystubs'); clearSelection(); }}
        >
          <Banknote className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
          <p className="text-xl font-bold text-emerald-500">{(archiveSummary.archived_paystubs || 0) + (archiveSummary.paid_paystubs || 0)}</p>
          <p className="text-xs text-[var(--text-muted)]">Paid Stubs</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'workers' ? 'ring-2 ring-red-400' : ''}`}
          onClick={() => { setArchiveTab('workers'); clearSelection(); }}
        >
          <Users className="w-5 h-5 mx-auto mb-1 text-[var(--error)]" />
          <p className="text-xl font-bold text-[var(--error)]">{archiveSummary.deleted_workers || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Workers</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'jobs' ? 'ring-2 ring-orange-400' : ''}`}
          onClick={() => { setArchiveTab('jobs'); clearSelection(); }}
        >
          <Briefcase className="w-5 h-5 mx-auto mb-1 text-orange-500" />
          <p className="text-xl font-bold text-orange-500">{archiveSummary.deleted_jobs || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Jobs</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'cancelled' ? 'ring-2 ring-yellow-400' : ''}`}
          onClick={() => { setArchiveTab('cancelled'); clearSelection(); }}
        >
          <X className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
          <p className="text-xl font-bold text-yellow-500">{archiveSummary.cancelled_bookings || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Cancelled Bookings</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'transactions' ? 'ring-2 ring-purple-400' : ''}`}
          onClick={() => { setArchiveTab('transactions'); clearSelection(); }}
        >
          <DollarSign className="w-5 h-5 mx-auto mb-1 text-[#F4C2C2]" />
          <p className="text-xl font-bold text-[#F4C2C2]">{archiveSummary.deleted_transactions || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Txns</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'applications' ? 'ring-2 ring-blue-400' : ''}`}
          onClick={() => { setArchiveTab('applications'); clearSelection(); }}
        >
          <FileText className="w-5 h-5 mx-auto mb-1 text-[#D4AF37]" />
          <p className="text-xl font-bold text-[#D4AF37]">{archiveSummary.deleted_applications || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Apps</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'conversations' ? 'ring-2 ring-pink-400' : ''}`}
          onClick={() => { setArchiveTab('conversations'); clearSelection(); }}
        >
          <MessageSquare className="w-5 h-5 mx-auto mb-1 text-pink-500" />
          <p className="text-xl font-bold text-pink-500">{archiveSummary.archived_conversations || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Chats</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'emails' ? 'ring-2 ring-indigo-400' : ''}`}
          onClick={() => { setArchiveTab('emails'); clearSelection(); }}
        >
          <Mail className="w-5 h-5 mx-auto mb-1 text-indigo-500" />
          <p className="text-xl font-bold text-indigo-500">{archiveSummary.deleted_emails || archivedEmails.length || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Emails</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'idcards' ? 'ring-2 ring-cyan-400' : ''}`}
          onClick={() => { setArchiveTab('idcards'); clearSelection(); }}
        >
          <CreditCard className="w-5 h-5 mx-auto mb-1 text-cyan-500" />
          <p className="text-xl font-bold text-cyan-500">{(archiveSummary.deleted_id_cards || 0) + (archiveSummary.revoked_id_cards || 0)}</p>
          <p className="text-xs text-[var(--text-muted)]">Archived ID Cards</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'services' ? 'ring-2 ring-teal-400' : ''}`}
          onClick={() => { setArchiveTab('services'); clearSelection(); }}
        >
          <Sparkles className="w-5 h-5 mx-auto mb-1 text-teal-500" />
          <p className="text-xl font-bold text-teal-500">{deletedServices.length || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Services</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'policies' ? 'ring-2 ring-slate-400' : ''}`}
          onClick={() => { setArchiveTab('policies'); clearSelection(); }}
        >
          <ScrollText className="w-5 h-5 mx-auto mb-1 text-slate-500" />
          <p className="text-xl font-bold text-slate-500">{deletedPolicies.length || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Policies</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'coupons' ? 'ring-2 ring-amber-400' : ''}`}
          onClick={() => { setArchiveTab('coupons'); clearSelection(); }}
        >
          <Tag className="w-5 h-5 mx-auto mb-1 text-amber-500" />
          <p className="text-xl font-bold text-amber-500">{deletedCoupons.length || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Deleted Coupons</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'receipts' ? 'ring-2 ring-cyan-400' : ''}`}
          onClick={() => { setArchiveTab('receipts'); clearSelection(); }}
        >
          <Receipt className="w-5 h-5 mx-auto mb-1 text-cyan-500" />
          <p className="text-xl font-bold text-cyan-500">{archivedReceipts.length || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Archived Receipts</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'tips' ? 'ring-2 ring-pink-400' : ''}`}
          onClick={() => { setArchiveTab('tips'); clearSelection(); }}
        >
          <Heart className="w-5 h-5 mx-auto mb-1 text-pink-500" />
          <p className="text-xl font-bold text-pink-500">{archivedTips.length || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Archived Tips</p>
        </div>
        <div 
          className={`card p-3 text-center cursor-pointer transition-all ${archiveTab === 'courier' ? 'ring-2 ring-purple-400' : ''}`}
          onClick={() => { setArchiveTab('courier'); clearSelection(); }}
        >
          <Truck className="w-5 h-5 mx-auto mb-1 text-[#F4C2C2]" />
          <p className="text-xl font-bold text-[#F4C2C2]">{archivedCourierJobs.length || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Archived Courier</p>
        </div>
      </div>
      )}

      {/* Select All Button when in select mode */}
      {selectMode && getCurrentItems().length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => selectAll(getCurrentItems())}
          >
            Select All ({getCurrentItems().length})
          </Button>
          <span className="text-sm text-[var(--text-muted)]">
            {selectedItems.length} selected
          </span>
        </div>
      )}

      {archiveLoading ? (
        <div className="card p-12 text-center">
          <div className="animate-pulse-soft text-[var(--primary)]">Loading archive...</div>
        </div>
      ) : (
        <>
          {/* Completed Jobs */}
          {archiveTab === 'completed' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-[var(--success)]/10 border-b border-green-100">
                <h2 className="font-semibold text-green-700 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Completed Jobs ({completedJobs.length})
                </h2>
              </div>
              {completedJobs.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No completed jobs yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {completedJobs.map(job => (
                    <div key={job.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              job.service_type === 'private_concierge' || job.service_type === 'social_engagement' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-[var(--success)]/15 text-green-700'
                            }`}>
                              {job.service_type?.toUpperCase()}
                            </span>
                            <p className="font-semibold">{job.service_name || job.service_type}</p>
                          </div>
                          <p className="text-sm text-[var(--text-muted)]">
                            {job.scheduled_date} @ {job.scheduled_time} ({job.duration_hours}h)
                          </p>
                          <p className="text-sm">
                            Customer: {job.customer_info?.full_name || job.customer_name || 'N/A'}
                          </p>
                          {job.worker_info && (
                            <p className="text-sm text-[var(--primary)]">
                              Worker: {job.worker_info.full_name}
                            </p>
                          )}
                          <p className="text-xs text-[var(--success)]">
                            Completed: {job.completed_at ? new Date(job.completed_at).toLocaleString() : 'N/A'}
                          </p>
                          <p className="text-sm font-medium">
                            Total: ${job.total_amount?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="px-3 py-1 bg-[var(--success)]/15 text-green-700 rounded-full text-sm font-medium">
                            Completed
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Deleted Workers */}
          {archiveTab === 'workers' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-[var(--error)]/10 border-b border-red-100">
                <h2 className="font-semibold text-red-700 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Deleted Workers ({deletedWorkers.length})
                </h2>
              </div>
              {deletedWorkers.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted workers</p>
                </div>
              ) : (
                <div className="divide-y">
                  {deletedWorkers.map(worker => (
                    <div key={worker.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        {selectMode && (
                          <button 
                            onClick={() => toggleSelectItem(worker.id)}
                            className="flex-shrink-0"
                          >
                            {selectedItems.includes(worker.id) ? (
                              <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{worker.full_name}</p>
                          <p className="text-sm text-[var(--text-muted)]">{worker.email}</p>
                          <p className="text-xs text-[var(--error)]">
                            Deleted: {new Date(worker.deleted_at).toLocaleString()}
                            {worker.deleted_by_info && ` by ${worker.deleted_by_info.full_name}`}
                          </p>
                          {worker.deletion_reason && (
                            <p className="text-xs text-[var(--text-muted)]">Reason: {worker.deletion_reason}</p>
                          )}
                        </div>
                        {!selectMode && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-[var(--success)] border-green-300 hover:bg-[var(--success)]/10"
                              onClick={() => handleRestoreWorker(worker)}
                            >
                              Restore
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handlePermanentDeleteWorker(worker)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Deleted Jobs */}
          {archiveTab === 'jobs' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-orange-50 border-b border-orange-100">
                <h2 className="font-semibold text-orange-700 flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Deleted Jobs ({deletedJobs.length})
                </h2>
              </div>
              {deletedJobs.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted jobs</p>
                </div>
              ) : (
                <div className="divide-y">
                  {deletedJobs.map(job => (
                    <div key={job.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        {selectMode && (
                          <button 
                            onClick={() => toggleSelectItem(job.id)}
                            className="flex-shrink-0"
                          >
                            {selectedItems.includes(job.id) ? (
                              <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{job.service_name || job.service_type}</p>
                          <p className="text-sm text-[var(--text-muted)]">
                            {job.scheduled_date} @ {job.scheduled_time} ({job.duration_hours}h)
                          </p>
                          <p className="text-sm">
                            Customer: {job.customer_info?.full_name || job.customer_name || 'N/A'}
                          </p>
                          <p className="text-xs text-orange-500">
                            Deleted: {new Date(job.deleted_at).toLocaleString()}
                            {job.deleted_by_info && ` by ${job.deleted_by_info.full_name}`}
                          </p>
                          <p className="text-xs text-[var(--text-muted)]">
                            Previous Status: {job.status_before_deletion}
                          </p>
                        </div>
                        {!selectMode && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-[var(--success)] border-green-300 hover:bg-[var(--success)]/10"
                              onClick={() => handleRestoreJob(job)}
                            >
                              Restore
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handlePermanentDeleteJob(job)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Cancelled Bookings */}
          {archiveTab === 'cancelled' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-[var(--warning)]/10 border-b border-yellow-100">
                <h2 className="font-semibold text-yellow-700 flex items-center gap-2">
                  <X className="w-5 h-5" />
                  Cancelled Bookings ({cancelledBookings.length})
                </h2>
              </div>
              {cancelledBookings.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No cancelled bookings</p>
                </div>
              ) : (
                <div className="divide-y">
                  {cancelledBookings.map(booking => (
                    <div key={booking.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              booking.service_type === 'private_concierge' || booking.service_type === 'social_engagement' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-[var(--success)]/15 text-green-700'
                            }`}>
                              {booking.service_type?.toUpperCase()}
                            </span>
                            <p className="font-semibold">{booking.service_name || 'Service'}</p>
                          </div>
                          <p className="text-sm text-[var(--text-muted)]">
                            {booking.scheduled_date} @ {booking.scheduled_time}
                          </p>
                          <p className="text-sm">
                            Customer: {booking.customer_info?.full_name || booking.customer_name || 'N/A'}
                          </p>
                          {booking.worker_info && (
                            <p className="text-sm text-[var(--text-muted)]">
                              Worker: {booking.worker_info.full_name}
                            </p>
                          )}
                          <p className="text-xs text-[var(--warning)]">
                            Cancelled: {booking.cancelled_at ? new Date(booking.cancelled_at).toLocaleString() : 'N/A'}
                          </p>
                          {booking.cancellation_reason && (
                            <p className="text-xs text-[var(--text-muted)]">Reason: {booking.cancellation_reason}</p>
                          )}
                          <p className="text-sm font-medium text-[var(--primary)]">
                            Total: ${booking.total_amount?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleArchiveCancelledBooking(booking)}
                          >
                            <Archive className="w-4 h-4 mr-1" />
                            Archive
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Deleted Transactions */}
          {archiveTab === 'transactions' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-[#F4C2C2]/10 border-b border-purple-100">
                <h2 className="font-semibold text-purple-700 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Deleted Transactions ({deletedTransactions.length})
                </h2>
              </div>
              {deletedTransactions.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted transactions</p>
                </div>
              ) : (
                <div className="divide-y">
                  {deletedTransactions.map(txn => (
                    <div key={txn.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        {selectMode && (
                          <button 
                            onClick={() => toggleSelectItem(txn.id)}
                            className="flex-shrink-0"
                          >
                            {selectedItems.includes(txn.id) ? (
                              <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{txn.category || txn.type}</p>
                          <p className="text-sm text-[var(--text-muted)]">{txn.description}</p>
                          <p className="text-lg font-bold text-[var(--error)]">-${txn.amount?.toFixed(2)}</p>
                          <p className="text-xs text-[#F4C2C2]">
                            Deleted: {new Date(txn.deleted_at).toLocaleString()}
                            {txn.deleted_by_info && ` by ${txn.deleted_by_info.full_name}`}
                          </p>
                        </div>
                        {!selectMode && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-[var(--success)] border-green-300 hover:bg-[var(--success)]/10"
                              onClick={() => handleRestoreTransaction(txn)}
                            >
                              Restore
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handlePermanentDeleteTransaction(txn)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Deleted Applications */}
          {archiveTab === 'applications' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-[#D4AF37]/10 border-b border-blue-100">
                <h2 className="font-semibold text-[#D4AF37] flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Deleted Applications ({deletedApplications?.length || 0})
                </h2>
              </div>
              {(!deletedApplications || deletedApplications.length === 0) ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted applications</p>
                </div>
              ) : (
                <div className="divide-y">
                  {deletedApplications.map(app => (
                    <div key={app.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        {selectMode && (
                          <button 
                            onClick={() => toggleSelectItem(app.id)}
                            className="flex-shrink-0"
                          >
                            {selectedItems.includes(app.id) ? (
                              <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{app.full_name}</p>
                          <p className="text-sm text-[var(--text-muted)]">{app.email} | {app.phone}</p>
                          <p className="text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              app.status_before_deletion === 'approved' ? 'bg-[var(--success)]/15 text-green-700' :
                              app.status_before_deletion === 'rejected' ? 'bg-[var(--error)]/15 text-red-700' :
                              'bg-[var(--warning)]/15 text-yellow-700'
                            }`}>
                              Was: {app.status_before_deletion || 'pending'}
                            </span>
                          </p>
                          <p className="text-xs text-[#D4AF37] mt-1">
                            Deleted: {new Date(app.deleted_at).toLocaleString()}
                            {app.deleted_by_info && ` by ${app.deleted_by_info.full_name}`}
                          </p>
                        </div>
                        {!selectMode && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-[var(--success)] border-green-300 hover:bg-[var(--success)]/10"
                              onClick={() => handleRestoreApplication(app)}
                            >
                              Restore
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handlePermanentDeleteApplication(app)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Archived Conversations */}
          {archiveTab === 'conversations' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-pink-50 border-b border-pink-100">
                <h2 className="font-semibold text-pink-700 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Deleted Conversations ({archivedConversations?.length || 0})
                </h2>
              </div>
              {(!archivedConversations || archivedConversations.length === 0) ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted conversations</p>
                </div>
              ) : (
                <div className="divide-y">
                  {archivedConversations.map(conv => (
                    <div key={conv.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        {selectMode && (
                          <button 
                            onClick={() => toggleSelectItem(conv.id)}
                            className="flex-shrink-0"
                          >
                            {selectedItems.includes(conv.id) ? (
                              <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          conv.conversation_type === 'group' ? 'bg-[#F4C2C2]/15' :
                          conv.conversation_type === 'support' ? 'bg-[var(--primary)]/10' :
                          'bg-[#D4AF37]/10'
                        }`}>
                          <MessageSquare className={`w-5 h-5 ${
                            conv.conversation_type === 'group' ? 'text-[#F4C2C2]' :
                            conv.conversation_type === 'support' ? 'text-[var(--primary)]' :
                            'text-[#D4AF37]'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">
                            {conv.conversation_type === 'group' 
                              ? conv.group_name || 'Group Chat'
                              : conv.participant_names 
                                ? Object.values(conv.participant_names).filter(n => n !== 'NC Help Hub').join(', ') || 'Chat'
                                : 'Conversation'
                            }
                          </p>
                          <p className="text-sm text-[var(--text-muted)] capitalize">
                            {conv.conversation_type} • {conv.participants?.length || 0} participants
                          </p>
                          {conv.last_message && (
                            <p className="text-sm text-[var(--text-muted)] truncate">
                              Last: {conv.last_message}
                            </p>
                          )}
                          <p className="text-xs text-pink-500 mt-1">
                            Archived: {new Date(conv.archived_at).toLocaleString()}
                          </p>
                        </div>
                        {!selectMode && (
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handlePermanentDeleteConversation(conv)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Paid Pay Stubs */}
          {archiveTab === 'paystubs' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-emerald-50 border-b border-emerald-100">
                <h2 className="font-semibold text-emerald-700 flex items-center gap-2">
                  <Banknote className="w-5 h-5" />
                  Paid Pay Stubs ({archivedPaystubs?.length || 0})
                </h2>
              </div>
              {(!archivedPaystubs || archivedPaystubs.length === 0) ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No paid pay stubs yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {archivedPaystubs.map(stub => (
                    <div key={stub.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        {selectMode && (
                          <button 
                            onClick={() => toggleSelectItem(stub.id)}
                            className="flex-shrink-0"
                          >
                            {selectedItems.includes(stub.id) ? (
                              <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Banknote className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{stub.worker_name}</p>
                          <p className="text-sm text-[var(--text-muted)]">
                            {stub.pay_period_start} to {stub.pay_period_end}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-lg font-bold text-emerald-600">${stub.net_pay?.toFixed(2)}</span>
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                              {stub.total_hours}h
                            </span>
                            {stub.payment_method && (
                              <span className="text-xs bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded capitalize">
                                {stub.payment_method.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          {stub.notes && (
                            <p className="text-xs text-[var(--text-muted)] mt-1 italic">
                              Note: {stub.notes}
                            </p>
                          )}
                          {stub.payment_notes && (
                            <p className="text-xs text-[#D4AF37] mt-1">
                              Payment Note: {stub.payment_notes}
                            </p>
                          )}
                          <p className="text-xs text-emerald-500 mt-1">
                            Paid: {new Date(stub.paid_at || stub.archived_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Deleted Emails */}
          {archiveTab === 'emails' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-indigo-50 border-b border-indigo-100">
                <h2 className="font-semibold text-indigo-700 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Deleted Emails ({archivedEmails?.length || 0})
                </h2>
              </div>
              {(!archivedEmails || archivedEmails.length === 0) ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted emails</p>
                </div>
              ) : (
                <div className="divide-y">
                  {archivedEmails.map(email => (
                    <div key={email.id} className={`p-4 hover:bg-[var(--background-subtle)] ${selectedItems.includes(email.id) ? 'bg-indigo-50' : ''}`}>
                      <div className="flex items-center gap-3">
                        {selectMode && (
                          <button 
                            onClick={() => toggleSelectItem(email.id)}
                            className="flex-shrink-0"
                          >
                            {selectedItems.includes(email.id) ? (
                              <CheckSquare className="w-5 h-5 text-[var(--primary)]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-indigo-500" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{email.subject}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              email.recipient_type === 'all_workers' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                              email.recipient_type === 'all_customers' ? 'bg-[var(--success)]/15 text-green-700' :
                              'bg-[#F4C2C2]/15 text-purple-700'
                            }`}>
                              {email.recipient_type?.replace('_', ' ')}
                            </span>
                            <span className="text-xs text-[var(--text-muted)]">
                              {email.recipient_count} recipients
                            </span>
                          </div>
                          <p className="text-xs text-[var(--text-muted)] mt-1">
                            Sent: {new Date(email.sent_at).toLocaleString()}
                          </p>
                          {email.deleted_at && (
                            <p className="text-xs text-indigo-500 mt-1">
                              Deleted: {new Date(email.deleted_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {!selectMode && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleRestoreEmail(email)}
                              className="text-[var(--success)] border-green-300 hover:bg-[var(--success)]/10"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handlePermanentDeleteEmail(email)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Deleted Services */}
          {archiveTab === 'services' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-teal-50 border-b border-teal-100">
                <h2 className="font-semibold text-teal-700 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Deleted Services ({deletedServices?.length || 0})
                </h2>
              </div>
              {(!deletedServices || deletedServices.length === 0) ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted services</p>
                </div>
              ) : (
                <div className="divide-y">
                  {deletedServices.map(service => (
                    <div key={service.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          service.service_type === 'private_concierge' ? 'bg-[#D4AF37]/10' :
                          service.service_type === 'social_engagement' ? 'bg-[#F4C2C2]/15' : 'bg-[var(--success)]/15'
                        }`}>
                          <Sparkles className={`w-5 h-5 ${
                            service.service_type === 'private_concierge' ? 'text-[#D4AF37]' :
                            service.service_type === 'social_engagement' ? 'text-[#F4C2C2]' : 'text-[var(--success)]'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{service.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              service.service_type === 'private_concierge' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                              service.service_type === 'social_engagement' ? 'bg-[#F4C2C2]/15 text-purple-700' :
                              'bg-[var(--success)]/15 text-green-700'
                            }`}>
                              {service.service_type === 'private_concierge' ? 'Private Concierge' :
                               service.service_type === 'social_engagement' ? 'Social Engagement' : 'Rides'}
                            </span>
                            <span className="text-xs text-[var(--text-muted)]">
                              ${service.hourly_rate}/hr • {service.duration_hours}h
                            </span>
                          </div>
                          {service.deleted_at && (
                            <p className="text-xs text-teal-500 mt-1">
                              Deleted: {new Date(service.deleted_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Deleted Policies */}
          {archiveTab === 'policies' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-100">
                <h2 className="font-semibold text-slate-700 flex items-center gap-2">
                  <ScrollText className="w-5 h-5" />
                  Deleted Policies ({deletedPolicies?.length || 0})
                </h2>
              </div>
              {(!deletedPolicies || deletedPolicies.length === 0) ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted policies</p>
                </div>
              ) : (
                <div className="divide-y">
                  {deletedPolicies.map(policy => (
                    <div key={policy.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <ScrollText className="w-5 h-5 text-slate-500" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{policy.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                              {policy.category}
                            </span>
                            {policy.is_required_reading && (
                              <span className="text-xs px-2 py-0.5 rounded bg-[var(--error)]/15 text-red-700">
                                Required
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-1">
                            {policy.content?.substring(0, 100)}...
                          </p>
                          {policy.deleted_at && (
                            <p className="text-xs text-slate-500 mt-1">
                              Deleted: {new Date(policy.deleted_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Deleted Coupons */}
          {archiveTab === 'coupons' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-[var(--warning)]/10 border-b border-amber-100">
                <h2 className="font-semibold text-amber-700 flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Deleted Coupons ({deletedCoupons?.length || 0})
                </h2>
              </div>
              {(!deletedCoupons || deletedCoupons.length === 0) ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No deleted coupons</p>
                </div>
              ) : (
                <div className="divide-y">
                  {deletedCoupons.map(coupon => (
                    <div key={coupon.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <Tag className="w-5 h-5 text-amber-500" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold font-mono">{coupon.code}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                              {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` : `$${coupon.discount_value} OFF`}
                            </span>
                            <span className="text-xs text-[var(--text-muted)]">
                              Used {coupon.times_used || 0}/{coupon.max_uses || '∞'} times
                            </span>
                          </div>
                          {coupon.deleted_at && (
                            <p className="text-xs text-amber-500 mt-1">
                              Deleted: {new Date(coupon.deleted_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Archived Receipts */}
          {archiveTab === 'receipts' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-cyan-50 border-b border-cyan-100">
                <h3 className="font-bold flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-cyan-600" />
                  Archived Receipts ({archivedReceipts.length})
                </h3>
              </div>
              {archivedReceipts.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  No archived receipts
                </div>
              ) : (
                <div className="divide-y">
                  {archivedReceipts.map(receipt => (
                    <div key={receipt.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-cyan-500" />
                          </div>
                          <div>
                            <p className="font-semibold">{receipt.service_name}</p>
                            <p className="text-sm text-[var(--text-muted)]">
                              ${receipt.receipt_amount} • {receipt.worker_name}
                            </p>
                            {receipt.archived_at && (
                              <p className="text-xs text-cyan-500">
                                Archived: {new Date(receipt.archived_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        {onRestoreReceipt && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => onRestoreReceipt(receipt.id)}
                            className="text-cyan-600"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" /> Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Archived Tips */}
          {archiveTab === 'tips' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-pink-50 border-b border-pink-100">
                <h3 className="font-bold flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-600" />
                  Archived Tips ({archivedTips.length})
                </h3>
              </div>
              {archivedTips.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  No archived tips
                </div>
              ) : (
                <div className="divide-y">
                  {archivedTips.map(tip => (
                    <div key={tip.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                            <Heart className="w-5 h-5 text-pink-500" />
                          </div>
                          <div>
                            <p className="font-semibold">${tip.amount?.toFixed(2)}</p>
                            <p className="text-sm text-[var(--text-muted)]">
                              For: {tip.worker_name} • From: {tip.customer_name}
                            </p>
                            {tip.note && (
                              <p className="text-xs italic text-[var(--text-secondary)] mt-1">"{tip.note}"</p>
                            )}
                            {tip.archived_at && (
                              <p className="text-xs text-pink-500">
                                Archived: {new Date(tip.archived_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        {onRestoreTip && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => onRestoreTip(tip.id)}
                            className="text-pink-600"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" /> Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Archived Courier Jobs */}
          {archiveTab === 'courier' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-[#F4C2C2]/10 border-b border-purple-100">
                <h3 className="font-bold flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#F4C2C2]" />
                  Archived Courier Jobs ({archivedCourierJobs.length})
                </h3>
              </div>
              {archivedCourierJobs.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  No archived courier jobs
                </div>
              ) : (
                <div className="divide-y">
                  {archivedCourierJobs.map(job => (
                    <div key={job.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#F4C2C2]/15 rounded-full flex items-center justify-center">
                            <Truck className="w-5 h-5 text-[#F4C2C2]" />
                          </div>
                          <div>
                            <p className="font-semibold">{job.item_description}</p>
                            <p className="text-sm text-[var(--text-muted)]">
                              {job.service_type?.replace('_', ' ')} • {job.customer_name}
                            </p>
                            <p className="text-xs text-[var(--text-secondary)]">
                              {job.pickup_address} → {job.delivery_address}
                            </p>
                            {job.archived_at && (
                              <p className="text-xs text-[#F4C2C2]">
                                Archived: {new Date(job.archived_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        {onRestoreCourierJob && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => onRestoreCourierJob(job.id)}
                            className="text-[#F4C2C2]"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" /> Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Archived ID Cards */}
          {archiveTab === 'idcards' && (
            <div className="card overflow-hidden">
              <div className="p-4 bg-cyan-50 border-b border-cyan-100">
                <h3 className="font-bold flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-cyan-600" />
                  Archived ID Cards ({archivedIDCards.length})
                </h3>
              </div>
              {archivedIDCards.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-muted)]">
                  No archived or revoked ID cards
                </div>
              ) : (
                <div className="divide-y">
                  {archivedIDCards.map(card => (
                    <div key={card.id} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            card.status === 'revoked' ? 'bg-[var(--error)]/15' : 'bg-cyan-100'
                          }`}>
                            <CreditCard className={`w-5 h-5 ${
                              card.status === 'revoked' ? 'text-[var(--error)]' : 'text-cyan-500'
                            }`} />
                          </div>
                          <div>
                            <p className="font-semibold">{card.worker_name}</p>
                            <p className="text-sm font-mono text-[var(--primary)]">{card.employee_id}</p>
                            <p className="text-xs text-[var(--text-muted)]">
                              {card.worker_email} • {card.service_types?.join(', ')}
                            </p>
                            <p className="text-xs mt-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                card.status === 'revoked' 
                                  ? 'bg-[var(--error)]/15 text-red-700' 
                                  : 'bg-[var(--background-subtle)] text-[var(--text-primary)]'
                              }`}>
                                {card.status?.toUpperCase()}
                              </span>
                              {card.deleted_at && (
                                <span className="ml-2 text-[var(--text-muted)]">
                                  Deleted: {new Date(card.deleted_at).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {card.deleted_at && onRestoreIDCard && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => onRestoreIDCard(card.id)}
                              className="text-cyan-600"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" /> Restore
                            </Button>
                          )}
                          {card.deleted_at && onPermanentDeleteIDCard && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => onPermanentDeleteIDCard(card.id)}
                              className="text-[var(--error)] hover:bg-[var(--error)]/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default ArchiveTab;
